import { useState, useEffect } from 'react';
import { useTranslations } from 'use-intl';
import { useNavigate } from '@tanstack/react-router';
import {
  AlertDialog,
  Body,
  Button,
  DataTable,
  DropdownMenu,
  DropdownMenuItem,
  I,
  PageHeader,
  PageHeaderSkeleton,
  Pill,
  Row,
  SearchInput,
  Stack,
  useDelayedLoading,
} from '@leanin/ui';
import { Tooltip } from '@leanin/ui/client';
import { InviteMemberModal } from '../../group/InviteMemberModal';
import { GroupPendingInvitesCard } from '../../group/admin/GroupPendingInvitesCard';
import { CopyJoinLinkButton } from '../../group/admin/CopyJoinLinkButton';
import { useMessageMember } from '../../groups/use-message-member';
import type { GroupMemberView } from '../../../lib/views/groups';
import { useInfiniteGroupMembers, useNetwork } from '../../../lib/queries/groups';
import {
  useLeaveGroup,
  useRemoveGroupMember,
  useSetGroupMemberRole,
} from '../../../lib/mutations/groups';
import { useGroupLeaveFlow } from '../../group/use-group-leave-flow';
import { formatGroupMemberError } from '../../groups/format-group-member-error';
import { useConfirmDialog } from '../../../lib/use-confirm-dialog';
import {
  MembersTableSkeleton,
  buildMemberColumns,
  filterMembersBySearch,
  memberName,
} from '../../group/admin/members-table';

type Props = {
  readonly networkId: string;
  readonly viewerUserId: string;
};

export function NetworkMembersSection({ networkId, viewerUserId }: Props) {
  const t = useTranslations('Group');
  const tn = useTranslations('Networks');
  const detailQuery = useNetwork(networkId);
  const membersQuery = useInfiniteGroupMembers(networkId, { pageSize: 100 });

  // Auto-fetch remaining pages so the admin always sees the full roster.
  // Networks with >100 members would otherwise silently truncate the list.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = membersQuery;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const navigate = useNavigate();
  const setRoleMutation = useSetGroupMemberRole();
  const removeMutation = useRemoveGroupMember();
  const leaveMutation = useLeaveGroup();
  const { messageMember, error: messageError } = useMessageMember();

  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const remove = useConfirmDialog<GroupMemberView>();
  const demote = useConfirmDialog<GroupMemberView>();

  const leaveFlow = useGroupLeaveFlow({
    leave: (handlers) =>
      leaveMutation.mutate(
        { id: networkId, kind: 'network' },
        {
          onSuccess: () => {
            navigate({ to: '/networks' });
            handlers.onSuccess();
          },
          onError: handlers.onError,
        },
      ),
    fallbackMessage: tn('membersAdmin.errors.leaveFailed'),
  });

  const isPending = setRoleMutation.isPending || removeMutation.isPending;

  const handlePromote = (m: GroupMemberView) => {
    setError(null);
    setRoleMutation.mutate(
      { groupId: networkId, kind: 'network', userId: m.userId, role: 'leader' },
      {
        onError: (err) =>
          setError(formatGroupMemberError(err, tn('membersAdmin.errors.roleUpdateFailed'), t('membersAdmin.lastLeaderNudge'))),
      },
    );
  };

  const confirmDemote = () => {
    if (demote.target === null) return;
    const target = demote.target;
    setError(null);
    setRoleMutation.mutate(
      { groupId: networkId, kind: 'network', userId: target.userId, role: 'member' },
      {
        onSettled: () => demote.close(),
        onError: (err) =>
          setError(formatGroupMemberError(err, tn('membersAdmin.errors.roleUpdateFailed'), t('membersAdmin.lastLeaderNudge'))),
      },
    );
  };

  const confirmRemove = () => {
    if (remove.target === null) return;
    const target = remove.target;
    setError(null);
    removeMutation.mutate(
      { groupId: networkId, userId: target.userId },
      {
        onSettled: () => remove.close(),
        onError: (err) =>
          setError(formatGroupMemberError(err, tn('membersAdmin.errors.removeFailed'), t('membersAdmin.lastLeaderNudge'))),
      },
    );
  };

  const showSkeleton = useDelayedLoading(membersQuery.data === undefined);
  if (showSkeleton) {
    return (
      <Stack gap="lg">
        <PageHeaderSkeleton withAction />
        <MembersTableSkeleton rows={6} />
      </Stack>
    );
  }
  if (membersQuery.data === undefined) return null;

  const allMembers = membersQuery.data?.pages.flatMap((p) => p.items) ?? [];
  const members = filterMembersBySearch(allMembers, search);
  const networkName = detailQuery.data?.name ?? tn('membersAdmin.networkNameFallback');

  // Leave-button gating: hide entirely if the viewer isn't on the roster
  // (e.g. platform admin viewing without joining), disabled + tooltip if
  // they're the sole leader, enabled otherwise.
  const viewerMembership = allMembers.find((m) => m.userId === viewerUserId);
  const viewerIsOnRoster = viewerMembership !== undefined;
  const leaderCount = allMembers.filter((m) => m.role === 'leader').length;
  const viewerIsOnlyLeader =
    viewerMembership?.role === 'leader' && leaderCount <= 1;

  const columns = buildMemberColumns<GroupMemberView>({
    viewerUserId,
    t,
    renderRole: (m) => (
      <Pill tone={m.role === 'leader' ? 'burgundy' : 'neutral'}>
        {m.role === 'leader' ? tn('membersAdmin.roleLeader') : tn('membersAdmin.roleMember')}
      </Pill>
    ),
    renderActions: (m, { name }) => {
      const isLeader = m.role === 'leader';
      return (
        <Row gap="xs" align="center" justify="flex-end">
          <Button
            variant="ghost"
            size="sm"
            icon={I.chat}
            aria-label={tn('membersAdmin.messageAria', { name })}
            onClick={() => messageMember(m.userId)}
          />
          <DropdownMenu
            align="end"
            trigger={
              <Button
                variant="ghost"
                size="sm"
                icon={I.more}
                aria-label={tn('membersAdmin.manageAria', { name })}
              />
            }
          >
            {!isLeader ? (
              <DropdownMenuItem
                onSelect={() => handlePromote(m)}
                disabled={isPending}
              >
                {tn('membersAdmin.makeLeader')}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                onSelect={() => demote.request(m)}
                disabled={isPending}
              >
                {tn('membersAdmin.demote')}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              destructive
              disabled={isPending}
              onSelect={() => remove.request(m)}
            >
              {tn('membersAdmin.removeFromNetwork')}
            </DropdownMenuItem>
          </DropdownMenu>
        </Row>
      );
    },
  });

  return (
    <Stack gap="lg">
      <PageHeader
        title={tn('membersAdmin.title')}
        subtitle={tn('membersAdmin.subtitle')}
        actions={
          <Row gap="sm" align="center">
            {!viewerIsOnRoster ? null : viewerIsOnlyLeader ? (
              <Tooltip content={tn('membersAdmin.onlyLeaderTooltip')}>
                <span tabIndex={0}>
                  <Button variant="secondary" size="md" disabled>
                    {tn('membersAdmin.leave')}
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button
                variant="secondary"
                size="md"
                onClick={leaveFlow.openConfirm}
              >
                {tn('membersAdmin.leave')}
              </Button>
            )}
            <CopyJoinLinkButton groupId={networkId} />
            <Button
              variant="primary"
              size="md"
              icon={I.plus}
              onClick={() => setInviteOpen(true)}
            >
              {tn('membersAdmin.addMember')}
            </Button>
          </Row>
        }
      />

      <Stack gap="md">
        <SearchInput
          placeholder={tn('membersAdmin.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 320 }}
        />
        <DataTable
          key={search}
          data={[...members]}
          columns={columns}
          pagination={{ pageSize: 10 }}
        >
          <DataTable.Header />
          <DataTable.Body emptyMessage={tn('membersAdmin.emptyMatch')} />
          <DataTable.Pagination />
        </DataTable>
        <Body size="sm" color="mute">
          {tn('membersAdmin.countSummary', {
            shown: members.length,
            total: detailQuery.data?.memberCount ?? allMembers.length,
          })}
        </Body>
      </Stack>

      {(error ?? messageError) !== null ? (
        <span role="alert" aria-live="polite">
          <Body size="sm" color="destructive">{error ?? messageError}</Body>
        </span>
      ) : null}

      <GroupPendingInvitesCard kind="network" groupId={networkId} />

      <InviteMemberModal
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        kind="network"
        groupId={networkId}
        groupName={networkName}
      />

      <AlertDialog
        open={remove.open}
        onOpenChange={remove.onOpenChange}
        title={
          remove.target !== null
            ? tn('membersAdmin.removeDialog.title', { name: memberName(remove.target) })
            : tn('membersAdmin.removeDialog.titleFallback')
        }
        description={
          remove.target !== null
            ? tn('membersAdmin.removeDialog.description', { name: memberName(remove.target) })
            : ''
        }
        actionLabel={tn('membersAdmin.removeDialog.action')}
        cancelLabel={tn('membersAdmin.removeDialog.cancel')}
        destructive
        onAction={confirmRemove}
      />

      <AlertDialog
        open={demote.open}
        onOpenChange={demote.onOpenChange}
        title={
          demote.target !== null
            ? tn('membersAdmin.demoteDialog.title', { name: memberName(demote.target) })
            : tn('membersAdmin.demoteDialog.titleFallback')
        }
        description={tn('membersAdmin.demoteDialog.description')}
        actionLabel={tn('membersAdmin.demoteDialog.action')}
        cancelLabel={tn('membersAdmin.demoteDialog.cancel')}
        destructive
        onAction={confirmDemote}
      />

      <AlertDialog
        open={leaveFlow.confirmOpen}
        onOpenChange={leaveFlow.setConfirmOpen}
        title={tn('membersAdmin.leaveDialog.title')}
        description={tn('membersAdmin.leaveDialog.description')}
        actionLabel={tn('membersAdmin.leaveDialog.action')}
        cancelLabel={tn('membersAdmin.leaveDialog.cancel')}
        destructive
        onAction={leaveFlow.confirmLeave}
      />
      {leaveFlow.error !== null ? (
        <Body size="sm" color="destructive">{leaveFlow.error}</Body>
      ) : null}
    </Stack>
  );
}
